/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { Avatar } from 'primeng/avatar';
import { environment } from '@ao/environments/environment';

import {
  Component,
  Input,
  OnInit,
  ViewChild,
  inject,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { PascalCasePipe } from '../../pipes/pascal-case.pipe';
import { LogoutService } from '@ao/app/core/services/logout.service';
import { LeftNavigationComponent } from '@ao/app/components/left-navigation/left-navigation.component';
import { appInsights } from '@ao/app/app.config';
import { NgClass, NgStyle } from '@angular/common';
import { CommonService } from '../../services/common.service';
import { ClientContextService } from '@ao/app/core/services/client-context.service';
import { ClientService } from '@ao/app/shared/services/client-service';
import { NavigationEnd, Router } from '@angular/router';
import { EXCLUDED_ROUTES } from '../../constants/excluded-routes';
import { SecureStorageService } from '@ao/app/core/services/secure-storage.service';

interface ProfileType {
  givenName?: string;
  surname?: string;
  userPrincipalName?: string;
  id?: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    MenubarModule,
    MenuModule,
    ButtonModule,
    PascalCasePipe,
    LeftNavigationComponent,
    Avatar,
    NgClass,
    NgStyle,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isLoginPage = false;
  @Input() isUnauthorizedPage = false;
  @Input() isRouteLoading = false;
  @Input() shouldShowClientSelection = false;
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private logoutService = inject(LogoutService);
  private commonService = inject(CommonService);
  private clientContext = inject(ClientContextService);
  private clientService = inject(ClientService);
  private secureStorage = inject(SecureStorageService);
  menuItems: MenuItem[];
  profile: ProfileType | undefined;
  loginDisplay = false;
  isSidebarVisible = false;
  @Input() roleName: string | undefined;
  clientName = 'Not selected';
  isFocused = false;
  showSwitchClientButton = false;
  constructor() {
    environment.favIcon;
    this.menuItems = [
      {
        label: 'Logout',
        icon: 'pi pi-power-off',
        command: () => this.logout(),
        ariaLabel: 'Logout button',
      },
    ];
  }
  @ViewChild('menu') menu: any;

  private scrollListener = () => {
    if (this.menu && this.menu.hide) {
      this.menu.hide();
    }
  };
  switchClient(): void {
    this.commonService.requestSwitchClient();
  }
  ngAfterViewInit() {
    window.addEventListener('scroll', this.scrollListener, true);
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.scrollListener, true);
  }

  ngOnInit(): void {
    // Migrate existing localStorage data to secure storage
    this.secureStorage.migrateFromLocalStorage([
      'profile',
      'accountId',
      'loggedInUserName',
      'loggedInUserEmail',
    ]);

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
        this.setLoginDisplay();
        this.loadProfile();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.loadProfile();
      });

    this.setLoginDisplay();
    this.loadProfile();
    this.setClientName(this.clientContext.getCurrentClient());
    this.clientContext.clientChanges$.subscribe(client => {
      this.setClientName(client);
    });

    const excludedRoutes = EXCLUDED_ROUTES;

    const checkShowSwitchClientButton = (url: string) => {
      const isExcluded = excludedRoutes.includes(url);
      if (!isExcluded) {
        this.clientService.getClients().subscribe(clients => {
          this.showSwitchClientButton = clients.length > 1;
        });
      } else {
        this.showSwitchClientButton = false;
      }
    };

    checkShowSwitchClientButton(this.router.url);

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        checkShowSwitchClientButton(event.urlAfterRedirects || event.url);
      }
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }
  private setClientName(client: any) {
    this.clientName = client?.name || 'Not selected';
  }
  loadProfile() {
    const account = this.authService.instance.getActiveAccount();
    if (account) {
      const storedProfile = this.secureStorage.getItem('profile');
      const storedAccountId = this.secureStorage.getItem('accountId');

      if (storedProfile && storedAccountId === account.homeAccountId) {
        this.profile = JSON.parse(storedProfile);
        if (this.profile?.givenName && this.profile?.surname)
          this.secureStorage.setItem(
            'loggedInUserName',
            `${this.profile?.givenName} ${this.profile?.surname}`
          );
        this.secureStorage.setItem(
          'loggedInUserEmail',
          this.profile?.userPrincipalName || ''
        );

        if (this.profile?.userPrincipalName) {
          appInsights.setAuthenticatedUserContext(
            this.profile.userPrincipalName
          );
        }
      } else {
        this.getProfile(environment.apiConfig.uri);
      }
    }
  }

  getProfile(url: string) {
    this.http.get<ProfileType>(url).subscribe(profile => {
      this.profile = profile;
      this.secureStorage.setItem('profile', JSON.stringify(profile));
      const account = this.authService.instance.getActiveAccount();
      if (account) {
        this.secureStorage.setItem('accountId', account.homeAccountId);
      }
    });
  }

  logout() {
    this.logoutService.logout();
  }

  handleMenuOpen() {
    setTimeout(() => {
      const menuItem = document.querySelector(
        '.p-menu .p-menuitem:first-child .p-menuitem-link'
      );
      if (menuItem) {
        (menuItem as HTMLElement).focus();
      }
    }, 100);
  }

  onMenuClose() {
    const profileButton = document.querySelector('.form p-button');
    if (profileButton) {
      (profileButton as HTMLElement).focus();
    }
  }

  handleSidebarOpen() {
    setTimeout(() => {
      const firstFocusable = document.querySelector(
        'app-left-navigation button, app-left-navigation a'
      );
      if (firstFocusable) {
        (firstFocusable as HTMLElement).focus();
      }
    }, 100);
  }

  handleSidebarClose() {
    const hamburgerButton = document.querySelector(
      'p-button[icon="pi pi-bars"]'
    );
    if (hamburgerButton) {
      (hamburgerButton as HTMLElement).focus();
    }
  }

  focusNextElement() {
    setTimeout(() => {
      const focusableElements =
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const elements = Array.from(
        document.querySelectorAll(focusableElements)
      ) as HTMLElement[];
      const currentIndex = elements.findIndex(
        el => document.activeElement === el
      );
      if (currentIndex > -1 && elements[currentIndex + 1]) {
        elements[currentIndex + 1].focus();
      }
    }, 0);
  }

  protected readonly environment = environment;
}
